import { MetaMessageTemplate } from "../../../../../model/integration/dto/meta/MetaMessageTemplate";
import { MetaTemplateParameters } from "../../../../../model/integration/dto/meta/MetaTemplateParameters";

type Component = MetaMessageTemplate["components"][number];

export type TemplateVariable = {
  /** Unique within a template, and the key its value is held under */
  key: string;
  area: "header" | "body" | "button" | "coupon" | "location";
  /** The placeholder's own token: "1" when positional, its name when named.
   * For a location header, which part of the place it is.
   */
  name: string;
  /** Which button it belongs to, for a URL button's placeholder or a
   * copy-code button's code
   */
  index?: number;
  /** What Meta approved the template with, shown as the input's placeholder so
   * the sender can see what belongs there
   */
  example?: string;
  /** Can be left empty */
  optional?: boolean;
};

const PLACEHOLDER = /{{\s*([\w]+)\s*}}/g;

const COORDINATES = /^\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*$/;

const tokensIn = (text?: string) => {
  const found: string[] = [];
  let match: RegExpExecArray | null;

  PLACEHOLDER.lastIndex = 0;

  while ((match = PLACEHOLDER.exec(text || "")) !== null) {
    if (!found.includes(match[1])) {
      found.push(match[1]);
    }
  }

  return found;
};

/** "40.7484, -73.9857", the way a map copies a place, as Meta's two strings */
export const coordinatesFrom = (text?: string) => {
  const match = COORDINATES.exec(text || "");

  if (!match) {
    return undefined;
  }

  const [, latitude, longitude] = match;

  if (Math.abs(Number(latitude)) > 90 || Math.abs(Number(longitude)) > 180) {
    return undefined;
  }

  return { latitude, longitude };
};

/** Every value the sender has to supply before this template can go out.
 *
 * Read from the text rather than from a list Meta provides, because that is
 * where the placeholders actually live - and it works the same whether the
 * template numbers them or names them.
 */
export const templateVariables = (
  template?: MetaMessageTemplate,
): TemplateVariable[] => {
  const components = template?.components || [];
  const variables: TemplateVariable[] = [];

  const header = components.find((c: Component) => c.type === "HEADER");
  const body = components.find((c: Component) => c.type === "BODY");
  const buttons =
    components.find((c: Component) => c.type === "BUTTONS")?.buttons || [];

  if (header?.format === "TEXT") {
    tokensIn(header.text).forEach((name, index) =>
      variables.push({
        key: `header:${name}`,
        area: "header",
        name,
        example: header.example?.header_text?.[index],
      }),
    );
  }

  // A location header is approved without a place: each send names its own
  if (header?.format === "LOCATION") {
    variables.push(
      {
        key: "location:coordinates",
        area: "location",
        name: "coordinates",
        example: "40.7484, -73.9857",
      },
      { key: "location:name", area: "location", name: "name", optional: true },
      {
        key: "location:address",
        area: "location",
        name: "address",
        optional: true,
      },
    );
  }

  tokensIn(body?.text).forEach((name, index) =>
    variables.push({
      key: `body:${name}`,
      area: "body",
      name,
      example: body?.example?.body_text?.[0]?.[index],
    }),
  );

  buttons.forEach((button, index) => {
    if (button.type === "COPY_CODE") {
      variables.push({
        key: `coupon:${index}`,
        area: "coupon",
        name: "code",
        index,
        example: [button.example].flat()[0],
      });
      return;
    }

    tokensIn(button.url).forEach((name) =>
      variables.push({
        key: `button:${index}:${name}`,
        area: "button",
        name,
        index,
      }),
    );
  });

  return variables;
};

/** Whether what has been typed is enough to send */
export const variableReady = (variable: TemplateVariable, value?: string) => {
  if (!value?.trim()) {
    return Boolean(variable.optional);
  }

  return variable.key !== "location:coordinates" || !!coordinatesFrom(value);
};

/** The same values in the shape the send endpoint takes */
export const templateParametersFrom = (
  variables: TemplateVariable[],
  values: Record<string, string>,
): MetaTemplateParameters | undefined => {
  if (!variables.length) {
    return undefined;
  }

  const parameters: MetaTemplateParameters = {
    header: [],
    body: [],
    buttons: [],
  };

  variables.forEach((variable) => {
    const value = values[variable.key] || "";

    if (variable.area === "location") {
      return;
    }

    if (variable.area === "button" || variable.area === "coupon") {
      parameters.buttons!.push({ index: variable.index!, value });
      return;
    }

    parameters[variable.area]!.push({ name: variable.name, value });
  });

  const coordinates = coordinatesFrom(values["location:coordinates"]);

  return {
    header: parameters.header?.length ? parameters.header : undefined,
    body: parameters.body?.length ? parameters.body : undefined,
    buttons: parameters.buttons?.length ? parameters.buttons : undefined,
    location: coordinates
      ? {
          ...coordinates,
          name: values["location:name"]?.trim() || undefined,
          address: values["location:address"]?.trim() || undefined,
        }
      : undefined,
  };
};

const sentValue = (
  variable: TemplateVariable,
  parameters?: MetaTemplateParameters,
) => {
  const location = parameters?.location;

  switch (variable.area) {
    case "location":
      if (variable.name === "coordinates") {
        return location && `${location.latitude}, ${location.longitude}`;
      }

      return variable.name === "name" ? location?.name : location?.address;
    case "button":
    case "coupon":
      return parameters?.buttons?.find(({ index }) => index === variable.index)
        ?.value;
    default:
      return parameters?.[variable.area]?.find(
        ({ name }) => name === variable.name,
      )?.value;
  }
};

/** The other way round: a sent template's values, keyed the way the send
 * modal keys its inputs
 */
export const templateValuesFrom = (
  template?: MetaMessageTemplate,
  parameters?: MetaTemplateParameters,
) => {
  const values: Record<string, string> = {};

  templateVariables(template).forEach((variable) => {
    const value = sentValue(variable, parameters);

    if (value) {
      values[variable.key] = value;
    }
  });

  return values;
};

/** The text as the customer will read it, with whatever has been typed so far
 * standing in for the placeholders. A button's link is filled per button, so
 * it goes by `button:<index>`.
 */
export const fillPlaceholders = (
  text?: string,
  area?: "header" | "body" | `button:${number}`,
  values?: Record<string, string>,
) => {
  if (!text || !values) {
    return text || "";
  }

  return text.replace(PLACEHOLDER, (placeholder, name) => {
    const value = values[`${area}:${name}`];

    return value || placeholder;
  });
};
